import { useMutation, useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import services from "./services";
import { CUSTOMERS, ORDER } from "./enums";
import useNotification from "../../utils/useNotification";
import { useOrdersStore } from "./store/useOrdersStore";
import { useGetOrderMediaConfig } from "../PlaceOrderTest/queryHooks";
import useMatricsStore from "./store/useMetricsStore";
import { useMemo, useCallback } from "react";
import { useConfiguredQuery } from "../../config/useConfiguredQuery";
import { extractOrdersFromFamilies } from "../../utils/orderNormalizer";
import { OrderRelationType } from "./store/orderStrategies";
import usePreferenceStore from "../../components/usePreferencesStore";
import useFiltersStore from "./store/useFiltersStore";
import {
  createTypesensePartSearchAdapter,
  performPartSearchStrategy,
} from "./utils/typesensePartSearch";


const isValidNumber = (value: unknown, min: number, max: number): boolean => {
  const numberValue = Number(value);
  return !isNaN(numberValue) && numberValue >= min && numberValue <= max;
};

export const useGetAllGaraazOrders = (filterString: string) => {
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const orderViewPreference = usePreferenceStore(
    (state) => state.orderViewPreference,
  );

  const setAllOrders = useOrdersStore((state) => state.setAllOrders);
  const setOrderItems = useOrdersStore((state) => state.setOrderItems);
  const setOrderEtas = useOrdersStore((state) => state.setOrderEtas);
  const setOrderRemarks = useOrdersStore((state) => state.setOrderRemarks);

  const currentISTDate = new Date().toLocaleString("en-US", {
    timeZone: "Asia/Kolkata",
  });
  const currentHourInIST = new Date(currentISTDate).getHours();

  const activeHoursStart = isValidNumber(
    mobileConfigs?.active_hours_start,
    0,
    23,
  )
    ? Number(mobileConfigs?.active_hours_start)
    : 7;

  const activeHoursEnd = isValidNumber(mobileConfigs?.active_hours_end, 0, 23)
    ? Number(mobileConfigs?.active_hours_end)
    : 20;

  const isValidActiveHours = activeHoursStart < activeHoursEnd;

  const finalActiveHoursStart = isValidActiveHours ? activeHoursStart : 7;
  const finalActiveHoursEnd = isValidActiveHours ? activeHoursEnd : 20;

  const isActiveHour =
    currentHourInIST >= finalActiveHoursStart &&
    currentHourInIST < finalActiveHoursEnd;

  const staleTime = isActiveHour
    ? isValidNumber(mobileConfigs?.allOrders_query_staleTime, 1, Infinity)
      ? Number(mobileConfigs?.allOrders_query_staleTime) * 60 * 1000
      : 1 * 60 * 1000
    : Infinity;

  const refetchInterval = isActiveHour
    ? isValidNumber(mobileConfigs?.allOrders_query_refetchInterval, 1, Infinity)
      ? Number(mobileConfigs?.allOrders_query_refetchInterval) * 60 * 1000
      : 5 * 60 * 1000
    : false;

  // Check if this is a search by order number (V2 API should be used)
  const isOrderNumberSearch = filterString.includes("orderNo=");

  return useQuery(
    [
      ORDER.ALLGARAAZORDERS,
      filterString,
      isOrderNumberSearch ? "v2" : "v3",
      orderViewPreference,
    ],
    () => {
      return isOrderNumberSearch
        ? services.getAllGaraazOrdersV2(filterString)
        : services.getAllGaraazOrders(filterString, orderViewPreference);
    },
    {
      refetchOnWindowFocus: "always",
      refetchOnMount: true,
      staleTime,
      refetchInterval,
      onSuccess: (data: any) => {
        // For V2 API responses, data structure is different (no need for family extraction)
        const normalized = isOrderNumberSearch
          ? data
          : extractOrdersFromFamilies(data);
        setAllOrders(normalized);
        setOrderItems(normalized, OrderRelationType.ROOT);
        setOrderEtas(normalized, OrderRelationType.ROOT);
        setOrderRemarks(normalized, OrderRelationType.ROOT);
      },
    },
  );
};

export function useGetUniqueCustomersToday(range: any) {
  const setUniqueCustomers = useMatricsStore(
    (state: any) => state.setUniqueCustomers,
  );

  const { data, isFetching } = useConfiguredQuery(
    [CUSTOMERS.UNIQUECUSTOMERSTODAY, range],
    () => services.getUniqueCustomers(range),
    {
      onSuccess: (data: any) => {
        setUniqueCustomers(data.today[0]);
      },
      refetchOnWindowFocus: false,
    },
  );

  return { data, isFetching };
}

export function useGetUniqueCustomersYesterdayAndThisMonth(range: any) {
  const { data, setUniqueCustomers, isDataExpired } = useMatricsStore();
  useEffect(() => {
    if (!data || isDataExpired()) {
      services.getUniqueCustomers(range).then((response) => {
        if (response && response.yearAndMonth) {
          setUniqueCustomers(response.yearAndMonth[0], true);
        }
      });
    }
  }, [range, isDataExpired]);

  return data;
}

export const useGetSingleGaraazOrder = (
  orderId: string,
  options?: { enabled?: boolean },
) => {
  return useQuery(
    [ORDER.SINGLEGARAAZORDER, orderId],
    () => services.getSingleOrder(orderId),
    { enabled: options?.enabled ?? true },
  );
};

export const usePartSearchStrategy = () => {
  const { data: mobileConfigs } = useGetOrderMediaConfig();

  const typesenseAdapter = useMemo(
    () => createTypesensePartSearchAdapter(mobileConfigs),
    [mobileConfigs],
  );

  return {
    execute: useCallback(
      (partNumber: string, page: number = 1, limit: number = 20) =>
        performPartSearchStrategy(partNumber, {
          typesenseAdapter,
          backendSearch: services.searchPartWithPartNumber,
          page,
          limit,
        }),
      [typesenseAdapter],
    ),
    isReady: !!mobileConfigs,
  };
};

export const useInfiniteSearchByPartNumber = (
  partNumber: string,
  limit: number = 20,
) => {
  const { execute, isReady } = usePartSearchStrategy();

  const query = useInfiniteQuery(
    ["searchPartInfinite", partNumber, isReady],
    ({ pageParam = 1 }) => execute(partNumber, pageParam, limit),
    {
      enabled: isReady && partNumber.trim() !== "",
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.length === limit ? allPages.length + 1 : undefined;
      },
    },
  );

  return {
    ...query,
    isLoading: query.isInitialLoading,
  };
};

export const useSearchByPartNumber = (partNumber: string) => {
  return useQuery([partNumber], async (): Promise<any> => {
    if (partNumber.trim() === "") {
      return [];
    }

    const response = await services.searchPartWithPartNumber(partNumber.trim());
    if (response === undefined) {
      return [];
    } else {
      return response;
    }
  });
};

export const useGetOrderHandlers = () => {
  const setOrderHandlers = useOrdersStore((state) => state.setOrderHandlers);
  return useQuery([ORDER.ORDERHANDLER], services.getOrderHandlers, {
    staleTime: Infinity,
    onSuccess: (data) => {
      setOrderHandlers(data);
    },
  });
};

export const useGetAllOrderHandlers = () => {
  return useQuery([ORDER.ALLORDERHANDLERS], services.getAllOrderHandlers, {
    staleTime: Infinity,
  });
};

export const useGetOrderStatsByOrderHandler = () => {
  return useQuery(
    [ORDER.ORDERSTATSBYORDERHANDLER],
    () => {
      return services.getOrderStatsByOrderHandler();
    },
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchInterval: 15 * 1000 * 60,
      staleTime: 15 * 1000 * 60,
      cacheTime: 30 * 1000 * 60,
    },
  );
};

export const useSplitOrder = (
  orderNo: string,
  orderId: string,
  orderItems: any,
  newOrderData: any,
) => {
  const toast = useNotification();

  return useMutation(
    () => {
      return services.splitOrder(
        orderNo,
        orderId,
        { orderItems: orderItems },
        newOrderData,
      );
    },

    {
      onSuccess: (data) => {
        toast({
          description: `New Order (${data.orderNo}) has been created successfully`,
          status: "success",
          duration: 10000,
          isClosable: true,
          position: "top",
        });
      },
      onError: (error) => {
        toast({ description: `${error}`, status: "error" });
      },
    },
  );
};

export const useGetAllSuppliers = () => {
  return useQuery([ORDER.GET_ALL_SUPPLIERS], services.getAllSuppliers, {
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useGetBrandsInfo = () => {
  return useQuery([ORDER.BRANDS_INFO], services.getBrandsInfo, {
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useGetAllMakers = () => {
  return useQuery([ORDER.CARMAKERS], services.fetchMakers, {
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useGetCustomerAllSubscriptions = (customerId: string) => {
  return useQuery(
    [customerId],
    () => services.getCustomerAllSubscriptions(customerId),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetDistinctCustomerStates = () => {
  return useQuery(
    [CUSTOMERS.DISTINCT_CUSTOMER_STATES],
    () => services.getDistinctCustomerStates(),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetChildOrders = (orderId: string, filterString: string) => {
  const setChildOrders = useOrdersStore((state) => state.setChildOrders);
  const setOrderItems = useOrdersStore((state) => state.setOrderItems);
  const setOrderEtas = useOrdersStore((state) => state.setOrderEtas);
  const setOrderRemarks = useOrdersStore((state) => state.setOrderRemarks);
  const toast = useNotification();

  return useQuery({
    queryKey: ["childOrders", orderId, filterString],
    queryFn: () => services.getChildOrders(orderId, filterString),
    enabled: false,
    onSuccess: (data) => {
      setChildOrders(orderId, data);
      setOrderItems(data, OrderRelationType.DESCENDANT, orderId);
      setOrderEtas(data, OrderRelationType.DESCENDANT, orderId);
      setOrderRemarks(data, OrderRelationType.DESCENDANT, orderId);
    },
    onError: (error: any) => {
      console.log(error);
      toast({
        title: "Error fetching child orders",
        description: error.message || "An unexpected error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });
};

export const useGetGrazeons = (userSubType?: string) => {
  return useQuery({
    queryKey: ["grazeons", userSubType],
    queryFn: async () => {
      return await services.getGrazeons(
        userSubType ? { userSubType } : undefined,
      );
    },
  });
};

export const useGetSupplierPartAvailability = (
  requestId: string,
  isOpen:boolean,
  partIds?: string[],
) => {
  return useQuery({
    queryKey: [ORDER.SUPPLIER_PART_AVAILABILITY, requestId, partIds ?? []],
    queryFn: () => services.getSupplierPartAvailability(requestId, partIds),
    enabled: Boolean(isOpen && requestId && partIds?.length),
  });
};
